/**
 * Study Navigation Store
 *
 * Singleton that tracks the ordered list of study IDs from the worklist
 * the user navigated from. Supports boundary prefetching — when nearing
 * the edge of the loaded list, fetches the next page in the background.
 */
let context = null;
/**
 * Back-stack for "Previous" navigation (browser-back semantics).
 * Forward navigation (Next, TC claim-next) pushes the current study here.
 * "Previous" pops from this stack; if empty, falls back to list-based adjacent.
 */
let historyStack = [];
/** How many studies from the edge to trigger a prefetch */
const PREFETCH_THRESHOLD = 3;
/**
 * Set the navigation context when the user navigates from a worklist to the viewer.
 * Call this from the studies worklist or teleconsultation worklist.
 */
export function setNavigationContext(params) {
    context = {
        studyIds: params.studyIds,
        total: params.total,
        currentPage: params.currentPage,
        pageSize: params.pageSize,
        fetchPage: params.fetchPage,
        loadedPages: new Set([params.currentPage]),
        pendingFetches: new Map(),
    };
    historyStack = [];
}
/** Check if a navigation context is available. */
export function hasNavigationContext() {
    return context !== null;
}
/**
 * Push a study onto the back-stack. Called before forward navigation
 * (Next in list, TC claim-next) so that Previous can return to it.
 */
export function pushToHistory(studyId) {
    // Avoid duplicates at the top (e.g., double-clicking Next)
    if (historyStack[historyStack.length - 1] !== studyId) {
        historyStack.push(studyId);
    }
}
/** Pop and return the most recent study from the back-stack, or null if empty. */
export function popFromHistory() {
    return historyStack.pop() ?? null;
}
/** Peek at the top of the back-stack without removing it. */
function peekHistory() {
    return historyStack.length > 0 ? historyStack[historyStack.length - 1] : null;
}
/**
 * Get the previous and next study IDs relative to the given study.
 *
 * "Previous" uses the back-stack first (browser-back semantics); if the stack
 * is empty it falls back to the adjacent study in the list.
 * "Next" is always the next study in the ordered list.
 */
export function getAdjacentStudies(currentStudyId) {
    // Previous: back-stack takes priority
    const historyPrevious = peekHistory();
    if (!context)
        return { previous: historyPrevious, next: null };
    const index = context.studyIds.indexOf(currentStudyId);
    if (index === -1)
        return { previous: historyPrevious, next: null };
    const listPrevious = index > 0 ? context.studyIds[index - 1] : null;
    const next = index < context.studyIds.length - 1 ? context.studyIds[index + 1] : null;
    return { previous: historyPrevious ?? listPrevious, next };
}
/**
 * Prefetch adjacent pages if the user is near the edge of the loaded list.
 * Returns true if a fetch was triggered.
 */
export async function prefetchIfNeeded(currentStudyId) {
    if (!context)
        return false;
    const index = context.studyIds.indexOf(currentStudyId);
    if (index === -1)
        return false;
    let fetched = false;
    // Near the end — fetch next page
    if (index >= context.studyIds.length - PREFETCH_THRESHOLD) {
        const hasMore = context.studyIds.length < context.total;
        if (hasMore) {
            const maxLoadedPage = Math.max(...context.loadedPages);
            const nextPage = maxLoadedPage + 1;
            if (!context.loadedPages.has(nextPage)) {
                fetched = true;
                await fetchAndAppend(nextPage, 'append');
            }
        }
    }
    // Near the start — fetch previous page
    if (index < PREFETCH_THRESHOLD) {
        const minLoadedPage = Math.min(...context.loadedPages);
        if (minLoadedPage > 1) {
            const prevPage = minLoadedPage - 1;
            if (!context.loadedPages.has(prevPage)) {
                fetched = true;
                await fetchAndAppend(prevPage, 'prepend');
            }
        }
    }
    return fetched;
}
/**
 * Per-tab record of where the user left the worklist for the viewer: which
 * study card hosted the launch and the worklist URL query string at that
 * moment. The viewer's "Study List" button peeks it to land back on the same
 * filtered/paged view; the worklist clears it on its first settled study list
 * and re-expands + scrolls to the card. sessionStorage rather than module
 * state because the worklist unmounts on the viewer route change and a reload
 * must not lose it. The record is written ONLY on the same-tab fallback
 * navigation (after openCockpit declines), so multi-monitor popup windows —
 * which inherit a copy of the opener's sessionStorage — never receive a live
 * one. Cleared on logout so a shared-workstation tab cannot leak one user's
 * reading position to the next.
 */
const WORKLIST_RETURN_KEY = 'worklist.returnState';
export function rememberWorklistReturn(state) {
    try {
        sessionStorage.setItem(WORKLIST_RETURN_KEY, JSON.stringify(state));
    }
    catch {
        // Storage unavailable — the card simply won't restore; never throw.
    }
}
export function peekWorklistReturn() {
    try {
        const raw = sessionStorage.getItem(WORKLIST_RETURN_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.studyId !== 'string' || typeof parsed.search !== 'string')
            return null;
        return { studyId: parsed.studyId, search: parsed.search };
    }
    catch {
        return null;
    }
}
export function clearWorklistReturn() {
    try {
        sessionStorage.removeItem(WORKLIST_RETURN_KEY);
    }
    catch {
        // Storage unavailable — nothing was stored anyway.
    }
}
/**
 * Teleconsultation twin of the worklist return record: which case row hosted
 * the launch and the TC worklist URL query string at that moment. Written only
 * on same-tab navigation to the viewer or report; the viewer's back button and
 * the report's "back to worklist" peek it to land on the same filtered/sorted
 * view, and the TC table clears it on its first settled list while re-expanding
 * the row. Cleared on logout alongside the worklist record.
 */
const TC_RETURN_KEY = 'tc.returnState';
export function rememberTcReturn(state) {
    try {
        sessionStorage.setItem(TC_RETURN_KEY, JSON.stringify(state));
    }
    catch {
        // Storage unavailable — the row simply won't restore; never throw.
    }
}
export function peekTcReturn() {
    try {
        const raw = sessionStorage.getItem(TC_RETURN_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.caseId !== 'string' || typeof parsed.search !== 'string')
            return null;
        return { caseId: parsed.caseId, search: parsed.search };
    }
    catch {
        return null;
    }
}
export function clearTcReturn() {
    try {
        sessionStorage.removeItem(TC_RETURN_KEY);
    }
    catch {
        // Storage unavailable — nothing was stored anyway.
    }
}
/** Where "back to worklist" lands: the remembered TC view, or the bare worklist. */
export function tcWorklistReturnUrl() {
    return `/teleconsultation${peekTcReturn()?.search ?? ''}`;
}
async function fetchAndAppend(page, direction) {
    if (!context)
        return;
    // Deduplicate — if this page is already being fetched, wait for that promise
    if (context.pendingFetches.has(page)) {
        await context.pendingFetches.get(page);
        return;
    }
    const fetchPromise = (async () => {
        try {
            const result = await context.fetchPage(page);
            if (!context)
                return; // Context cleared while fetching
            if (direction === 'append') {
                // Only add IDs we don't already have
                const existing = new Set(context.studyIds);
                const newIds = result.studyIds.filter((id) => !existing.has(id));
                context.studyIds = [...context.studyIds, ...newIds];
            }
            else {
                const existing = new Set(context.studyIds);
                const newIds = result.studyIds.filter((id) => !existing.has(id));
                context.studyIds = [...newIds, ...context.studyIds];
            }
            context.total = result.total;
            context.loadedPages.add(page);
        }
        finally {
            context?.pendingFetches.delete(page);
        }
    })();
    context.pendingFetches.set(page, fetchPromise);
    await fetchPromise;
}
